import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import { Button } from 'components/Button';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import Alert from 'components/Alert';
import { withNotification } from 'components/Notification';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import { Toggle } from 'components/Toggle';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { useStore } from 'context/store-context';
import { GENERATE_INSTALL_LINK, UPDATE_RETURN_INCENTIVE_SETTINGS } from 'gql/mutations';
import { useSettingsSaveState } from 'hooks/useSettingsSaveState';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  apiErrorHandler,
  NotificationTypes,
  SizeEnum,
  generateId,
  getStoreCurrencyFormatter,
} from 'utils';
import {
  XIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ShieldCheckIcon,
  PlusIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { SaveAsIcon } from '@heroicons/react/solid';
import {
  IncentiveOfferType,
  FeeType,
  FeeAppliesTo,
  FeeChargeBasis,
  FeeConditionField,
  FeeConditionOperator,
  FeeEligibilityScope,
  IncentiveFlowStage,
  IncentiveRule,
  IncentiveValueType,
  RefundOptimizationRule,
} from 'types/incentive';
import ReturnsSettingsTabs from './ReturnsSettingsTabs';
import SharedResolutionSettingsNotice from './SharedResolutionSettingsNotice';
import ExchangeIncentiveSettings, {ExchangeIncentiveSaveState} from './ExchangeIncentiveSettings';
import { normalizeAdditionalFeeAmount } from './IncentiveSettings.fees';
import {
  normalizeIncentiveRules,
  normalizeIncentiveValue,
  normalizeOptimizationRules,
  normalizePercentageValue,
} from './IncentiveSettings.percentages';
import {
  describeShippingCostConfig,
  getIncentiveConfigurationHighlights,
} from './IncentiveSettings.summary';
import { IncentiveItemEligibilityEditor } from './IncentiveItemEligibilityEditor';
import { summarizeIncentiveItemEligibility } from './IncentiveSettings.itemEligibility';
import { beginShopifyReauthorization } from 'utils/shopifyReauthorization';
import {
  DEFAULT_INCENTIVE_FLOW_ORDER,
  DynamicIncentiveFee,
  getIncentiveSettingsFormState,
  getIncentiveSettingsValidationError,
} from './IncentiveSettings.form';
import { DynamicFeeCard } from './DynamicFeeCard';

const INCENTIVE_NAME_MAX_LENGTH = 80;

const formatList = (items: string[]) => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const formatRange = (
  currency: string,
  minValue: number,
  maxValue?: number | null,
) => {
  if (minValue <= 0 && (maxValue === null || maxValue === undefined)) {
    return 'all return values';
  }

  if (minValue <= 0 && typeof maxValue === 'number') {
    return `up to ${getStoreCurrencyFormatter(currency, maxValue)}`;
  }

  if (maxValue === null || maxValue === undefined) {
    return `${getStoreCurrencyFormatter(currency, minValue)} and up`;
  }

  return `${getStoreCurrencyFormatter(
    currency,
    minValue,
  )} to ${getStoreCurrencyFormatter(currency, maxValue)}`;
};

const describeOfferValue = (
  currency: string,
  value: number,
  valueType: IncentiveValueType,
) => {
  return valueType === IncentiveValueType.PERCENTAGE
    ? `${value}%`
    : getStoreCurrencyFormatter(currency, value);
};

const describeOfferType = (offerType: IncentiveOfferType) => {
  return offerType === IncentiveOfferType.STORE_CREDIT
    ? 'store credit'
    : 'discount code';
};

const summarizeDeflectionRule = (currency: string, rule: IncentiveRule) => {
  const offers = rule.options.map((option) => {
    return `${describeOfferValue(
      currency,
      option.value,
      option.valueType,
    )} ${describeOfferType(option.offerType)}`;
  });

  return `Items ${formatRange(currency, rule.minItemValue, rule.maxItemValue)}${
    rule.requiresProtection ? ' with SHIPAID Delivery Guarantee' : ''
  }: ${formatList(offers)}. ${summarizeIncentiveItemEligibility(
    rule.eligibility,
  )}.`;
};

const summarizeOptimizationRule = (
  currency: string,
  rule: RefundOptimizationRule,
) => {
  return `Items ${formatRange(currency, rule.minItemValue, rule.maxItemValue)}${
    rule.requiresProtection ? ' with SHIPAID Delivery Guarantee' : ''
  }: +${rule.bonusValue}% store credit.`;
};

const RETURN_LABEL_CHARGE_SHIPPING_PATH =
  '/settings/resolution-portal/returns/carriers#return-label-charge';

const moveListItem = <T,>(items: T[], fromIndex: number, toIndex: number) => {
  if (toIndex < 0 || toIndex >= items.length) {
    return items;
  }

  const next = [...items];
  const [movedItem] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, movedItem);

  return next;
};

const IncentiveSettings = withNotification(
  ({ showNotification }: { showNotification: any }) => {
    const { storeId, storeDomain, storeProperties, refetchStoreProperties } =
      useStore();
    const pageOwner = useRef({storeId});
    if (pageOwner.current.storeId !== storeId) pageOwner.current = {storeId};
    const pageScope = pageOwner.current;
    const pageMounted = useRef(true);
    const combinedSave = useRef<{scope: object} | null>(null);
    if (combinedSave.current && combinedSave.current.scope !== pageScope) combinedSave.current = null;
    const [savingScope, setSavingScope] = useState<object | null>(null);
    const [exchangeState, setExchangeState] = useState<{scope: object; dirty: boolean; saving: boolean}>({scope: pageScope, dirty: false, saving: false});
    const exchangeApi = useRef<(ExchangeIncentiveSaveState & {scope: object}) | null>(null);
    const currentExchange = exchangeState.scope === pageScope ? exchangeState : {dirty: false, saving: false};
    const combinedSaving = savingScope === pageScope;
    useEffect(() => () => {pageMounted.current = false;}, []);
    const [updateStore, { loading: updateLoading }] = useMutation(UPDATE_RETURN_INCENTIVE_SETTINGS);
    const [generateInstallLink, { loading: reconnectingShopify }] = useMutation(
      GENERATE_INSTALL_LINK,
    );

    // Form state
    const [deflectionEnabled, setDeflectionEnabled] = useState(false);
    // State 1: Deflection Rules
    const [rules, setRules] = useState<IncentiveRule[]>([]);
    const [refundOptimizationEnabled, setRefundOptimizationEnabled] =
      useState(false);
    // State 2: Refund Optimization Rules
    const [optimizationRules, setOptimizationRules] = useState<
      RefundOptimizationRule[]
    >([]);
    const [flowOrder, setFlowOrder] = useState<IncentiveFlowStage[]>(
      DEFAULT_INCENTIVE_FLOW_ORDER,
    );
    const [smartEligibility, setSmartEligibility] = useState(true);

    // Stage 3 State
    const [allowAdditionalFees, setAllowAdditionalFees] = useState(false);
    const [waiveFeesForProtectedOrders, setWaiveFeesForProtectedOrders] =
      useState(false);
    const [dynamicFees, setDynamicFees] = useState<DynamicIncentiveFee[]>([]);
    const [expandedFeeIds, setExpandedFeeIds] = useState<string[]>([]);
    const [feeSampleSkus, setFeeSampleSkus] = useState<Record<string, string>>(
      {},
    );
    const formStoreIdRef = useRef<string | null>(null);
    const hydratedStoreIdRef = useRef<string | null>(null);
    const [incentiveHydrationKey, setIncentiveHydrationKey] = useState(
      () => `pending:${storeId || 'none'}`,
    );

    useEffect(() => {
      const nextStoreId = storeId || null;
      const storeChanged = formStoreIdRef.current !== nextStoreId;
      const applyFormState = (
        formState: ReturnType<typeof getIncentiveSettingsFormState>,
      ) => {
        setDeflectionEnabled(formState.deflectionEnabled);
        setRules(formState.rules);
        setRefundOptimizationEnabled(formState.refundOptimizationEnabled);
        setOptimizationRules(formState.optimizationRules);
        setFlowOrder(formState.flowOrder);
        setSmartEligibility(formState.smartEligibility);
        setAllowAdditionalFees(formState.allowAdditionalFees);
        setWaiveFeesForProtectedOrders(formState.waiveFeesForProtectedOrders);
        setDynamicFees(formState.dynamicFees);
      };

      if (storeChanged) {
        formStoreIdRef.current = nextStoreId;
        hydratedStoreIdRef.current = null;
        setExpandedFeeIds([]);
        setFeeSampleSkus({});
        setIncentiveHydrationKey(`pending:${nextStoreId || 'none'}`);
      }

      if (!storeId || !storeProperties || storeProperties.id !== storeId) {
        if (storeChanged) {
          applyFormState(getIncentiveSettingsFormState(undefined));
        }
        return;
      }

      if (hydratedStoreIdRef.current === storeId) {
        return;
      }

      applyFormState(
        getIncentiveSettingsFormState(storeProperties.incentiveSettings),
      );
      hydratedStoreIdRef.current = storeId;
      setIncentiveHydrationKey(`hydrated:${storeId}`);
    }, [storeId, storeProperties]);

    const incentiveFormState = useMemo(
      () => ({
        deflectionEnabled,
        rules,
        refundOptimizationEnabled,
        optimizationRules,
        flowOrder,
        smartEligibility,
        allowAdditionalFees,
        waiveFeesForProtectedOrders,
        dynamicFees,
      }),
      [
        allowAdditionalFees,
        deflectionEnabled,
        dynamicFees,
        flowOrder,
        optimizationRules,
        refundOptimizationEnabled,
        rules,
        smartEligibility,
        waiveFeesForProtectedOrders,
      ],
    );
    const {
      hasUnsavedChanges,
      saveStatus,
      captureSaveSnapshot,
      markSaved,
      markSaveFailed,
    } = useSettingsSaveState(incentiveFormState, incentiveHydrationKey);

    const handleAddFee = () => {
      const feeId = generateId();
      setDynamicFees([
        ...dynamicFees,
        {
          id: feeId,
          name: '',
          amount: 0,
          feeType: FeeType.FIXED,
          appliesTo: FeeAppliesTo.REFUND,
          enabled: true,
          chargeBasis: FeeChargeBasis.PER_RETURN,
          eligibility: {
            scope: FeeEligibilityScope.ALL_RETURNS,
            conditions: [],
          },
        },
      ]);
      setExpandedFeeIds((ids) => [...ids, feeId]);
    };

    const handleRemoveFee = (id: string) => {
      setDynamicFees(dynamicFees.filter((f) => f.id !== id));
    };

    const handleFeeChange = (
      id: string,
      field:
        | 'name'
        | 'amount'
        | 'feeType'
        | 'appliesTo'
        | 'enabled'
        | 'chargeBasis',
      value:
        | string
        | number
        | boolean
        | FeeType
        | FeeAppliesTo
        | FeeChargeBasis,
    ) => {
      setDynamicFees(
        dynamicFees.map((fee) => {
          if (fee.id !== id) return fee;

          if (field === 'amount') {
            return {
              ...fee,
              amount: normalizeAdditionalFeeAmount(Number(value), fee.feeType),
            };
          }

          if (field === 'feeType') {
            const feeType = value as FeeType;

            return {
              ...fee,
              feeType,
              amount: normalizeAdditionalFeeAmount(fee.amount, feeType),
            };
          }

          return { ...fee, [field]: value };
        }),
      );
    };

    const handleFeeScopeChange = (id: string, scope: FeeEligibilityScope) => {
      setDynamicFees(
        dynamicFees.map((fee) =>
          fee.id === id
            ? {
                ...fee,
                chargeBasis:
                  scope === FeeEligibilityScope.ALL_RETURNS
                    ? FeeChargeBasis.PER_RETURN
                    : fee.chargeBasis,
                eligibility: { ...fee.eligibility, scope },
              }
            : fee,
        ),
      );
    };

    const handleAddFeeCondition = (id: string) => {
      setDynamicFees(
        dynamicFees.map((fee) =>
          fee.id === id
            ? {
                ...fee,
                eligibility: {
                  ...fee.eligibility,
                  conditions: [
                    ...fee.eligibility.conditions,
                    {
                      id: generateId(),
                      field: FeeConditionField.SKU,
                      operator: FeeConditionOperator.EQUALS,
                      value: '',
                    },
                  ],
                },
              }
            : fee,
        ),
      );
    };

    const handleFeeConditionChange = (
      feeId: string,
      conditionId: string,
      field: 'operator' | 'value',
      value: FeeConditionOperator | string,
    ) => {
      setDynamicFees(
        dynamicFees.map((fee) =>
          fee.id === feeId
            ? {
                ...fee,
                eligibility: {
                  ...fee.eligibility,
                  conditions: fee.eligibility.conditions.map((condition) =>
                    condition.id === conditionId
                      ? { ...condition, [field]: value }
                      : condition,
                  ),
                },
              }
            : fee,
        ),
      );
    };

    const handleRemoveFeeCondition = (feeId: string, conditionId: string) => {
      setDynamicFees(
        dynamicFees.map((fee) =>
          fee.id === feeId
            ? {
                ...fee,
                eligibility: {
                  ...fee.eligibility,
                  conditions: fee.eligibility.conditions.filter(
                    (condition) => condition.id !== conditionId,
                  ),
                },
              }
            : fee,
        ),
      );
    };

    const toggleFeeEditor = (id: string) => {
      setExpandedFeeIds((ids) =>
        ids.includes(id)
          ? ids.filter((expandedId) => expandedId !== id)
          : [...ids, id],
      );
    };

    const activeDeflectionRules = useMemo(
      () => rules.filter((rule) => rule.isEnabled !== false),
      [rules],
    );
    const activeOptimizationRules = useMemo(
      () => optimizationRules.filter((rule) => rule.isEnabled !== false),
      [optimizationRules],
    );
    const activeDeflectionOfferCount = useMemo(
      () =>
        activeDeflectionRules.reduce(
          (count, rule) => count + rule.options.length,
          0,
        ),
      [activeDeflectionRules],
    );

    const storeCreditConfigured = useMemo(() => {
      const keepItemStoreCreditEnabled =
        deflectionEnabled &&
        activeDeflectionRules.some((rule) =>
          rule.options.some(
            (option) => option.offerType === IncentiveOfferType.STORE_CREDIT,
          ),
        );
      const baseStoreCreditEnabled = refundOptimizationEnabled;

      return keepItemStoreCreditEnabled || baseStoreCreditEnabled;
    }, [activeDeflectionRules, deflectionEnabled, refundOptimizationEnabled]);
    const storeCreditNeedsReconnect = Boolean(
      storeCreditConfigured && storeProperties?.shouldUpdateAppInstall,
    );

    const handleReconnectShopify = async () => {
      if (!storeDomain) {
        showNotification(
          NotificationTypes.error,
          'Select a Shopify store before reconnecting.',
        );
        return;
      }

      try {
        const { data } = await generateInstallLink({
          variables: { store: storeDomain },
        });
        beginShopifyReauthorization(data?.generateInstallLink);
      } catch (error) {
        apiErrorHandler(error as any);
        showNotification(
          NotificationTypes.error,
          'Could not start Shopify reauthorization.',
        );
      }
    };

    const validateReturnSave = (): boolean => {
      if (!storeProperties || storeProperties.id !== storeId) {
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          'The selected store is still loading. Please try again in a moment.',
        );
        return false;
      }

      if (storeCreditConfigured && storeProperties.shouldUpdateAppInstall) {
        markSaveFailed();
        showNotification(
          NotificationTypes.error,
          'Reconnect Shopify to enable store credit.',
        );
        return false;
      }

      const validationError = getIncentiveSettingsValidationError({
        deflectionEnabled,
        rules,
        refundOptimizationEnabled,
        optimizationRules,
        allowAdditionalFees,
        dynamicFees,
      });
      if (validationError) {
        markSaveFailed();
        showNotification(NotificationTypes.error, validationError);
        return false;
      }

      return true;
    };

    const handleSave = async (): Promise<boolean> => {
      const owns = () => pageMounted.current && pageOwner.current === pageScope;
      if (!owns() || !validateReturnSave() || !storeProperties || storeProperties.id !== storeId) return false;

      const submittedFormSnapshot = captureSaveSnapshot();
      const normalizedRules = normalizeIncentiveRules(rules);
      const normalizedOptimizationRules =
        normalizeOptimizationRules(optimizationRules);
      const firstLegacyKeepItemOption = normalizedRules[0]?.options?.[0];
      const firstLegacyBonusRule = normalizedOptimizationRules[0];
      const incentiveSettings = {
        flowOrder,
        returnDeflection: {
          enabled: deflectionEnabled,
          ...(firstLegacyKeepItemOption
            ? {
                keepItemIncentive: {
                  value: firstLegacyKeepItemOption.value,
                  offerType: firstLegacyKeepItemOption.offerType,
                },
              }
            : {}),
          rules: normalizedRules,
        },
        refundOptimization: {
          enabled: refundOptimizationEnabled,
          ...(firstLegacyBonusRule
            ? { storeCreditBonus: firstLegacyBonusRule.bonusValue }
            : {}),
          rules: normalizedOptimizationRules,
        },
        bounds: {
          smartEligibilityCheck: smartEligibility,
        },
        refundFallback: {
          allowAdditionalFees,
          waiveFeesForProtectedOrders,
          dynamicFees,
        },
      };

      try {
        const response = await updateStore({
          variables: {
            storeId,
            settings: incentiveSettings,
          },
        });
        if (!owns()) return false;
        if (response.errors?.length || response.data?.updateStore?.id !== storeId) {
          throw new Error('The incentive settings update was not confirmed. Please try again.');
        }
        markSaved(submittedFormSnapshot);
        showNotification(NotificationTypes.success, 'Incentives saved.');
        refetchStoreProperties?.();
        return true;
      } catch (error) {
        if (!owns()) return false;
        markSaveFailed();
        apiErrorHandler(error as any);
        showNotification(NotificationTypes.error, 'Could not save incentives.');
        return false;
      }
    };

    const saveAllSettings = async (): Promise<boolean> => {
      if (!pageMounted.current || pageOwner.current !== pageScope || combinedSave.current) return false;
      const api = exchangeApi.current?.scope === pageScope ? exchangeApi.current : null;
      const exchangeDirty = currentExchange.dirty;
      const returnDirty = hasUnsavedChanges;
      if ((returnDirty || !exchangeDirty) && !validateReturnSave()) return false;
      const token = {scope: pageScope};combinedSave.current = token;
      const owns = () => pageMounted.current && pageOwner.current === pageScope && combinedSave.current === token;
      setSavingScope(pageScope);
      try {
        if (exchangeDirty && (!api || !(await api.save()))) return false;
        if (!owns()) return false;
        if (exchangeDirty && !returnDirty) return true;
        if (!(await handleSave()) || !owns()) return false;
        // A Return save changes the whole-JSON CAS revision. Renew only the clean Exchange snapshot.
        return !api?.available || await api.refresh();
      } finally {
        if (owns()) {combinedSave.current = null;setSavingScope(null);}
      }
    };

    const currency = storeProperties?.currency || 'USD';
    const returnLabelChargeSettings = useMemo(() => {
      const shippingCost =
        storeProperties?.incentiveSettings?.refundFallback?.shippingCost;

      return [
        {
          title: 'Standard orders',
          description: 'Orders without Delivery Guarantee.',
          value: describeShippingCostConfig(
            currency,
            shippingCost?.unprotectedOrders,
          ),
          tone: 'gray',
        },
        {
          title: 'Guaranteed orders',
          description: 'Orders with Delivery Guarantee.',
          value: describeShippingCostConfig(
            currency,
            shippingCost?.protectedOrders,
          ),
          tone: 'green',
        },
      ];
    }, [currency, storeProperties?.incentiveSettings?.refundFallback]);
    const orderedOverviewCards = useMemo(() => {
      const configuredFeeCount = dynamicFees.length;
      const activeFeeCount = dynamicFees.filter(
        (fee) => fee.enabled !== false,
      ).length;
      const stageCards: Record<
        IncentiveFlowStage,
        {
          title: string;
          value: string;
          tone: 'blue' | 'green' | 'gray';
        }
      > = {
        [IncentiveFlowStage.RETURN_DEFLECTION]: {
          title: 'Keep-item offers',
          value: deflectionEnabled
            ? `${activeDeflectionOfferCount} offer${
                activeDeflectionOfferCount === 1 ? '' : 's'
              }`
            : 'Off',
          tone: 'blue',
        },
        [IncentiveFlowStage.REFUND_OPTIMIZATION]: {
          title: 'Store credit',
          value: refundOptimizationEnabled
            ? activeOptimizationRules.length > 0
              ? `${activeOptimizationRules.length} bonus rule${
                  activeOptimizationRules.length === 1 ? '' : 's'
                }`
              : 'On · standard credit'
            : 'Off',
          tone: 'green',
        },
      };

      return [
        stageCards[IncentiveFlowStage.RETURN_DEFLECTION],
        stageCards[IncentiveFlowStage.REFUND_OPTIMIZATION],
        {
          title: 'Additional fees',
          value: allowAdditionalFees
            ? activeFeeCount === configuredFeeCount
              ? `${configuredFeeCount} fee${
                  configuredFeeCount === 1 ? '' : 's'
                }`
              : `${activeFeeCount} of ${configuredFeeCount} active`
            : 'No fees',
          tone: 'gray' as const,
        },
      ];
    }, [
      allowAdditionalFees,
      activeDeflectionOfferCount,
      activeOptimizationRules.length,
      deflectionEnabled,
      dynamicFees,
      refundOptimizationEnabled,
    ]);
    const configurationHighlights = useMemo(
      () =>
        getIncentiveConfigurationHighlights({
          currency,
          deflectionEnabled,
          rules,
          refundOptimizationEnabled,
          optimizationRules,
          allowAdditionalFees,
          dynamicFees,
          waiveFeesForProtectedOrders,
          smartEligibility,
        }),
      [
        allowAdditionalFees,
        currency,
        deflectionEnabled,
        dynamicFees,
        optimizationRules,
        refundOptimizationEnabled,
        rules,
        smartEligibility,
        waiveFeesForProtectedOrders,
      ],
    );
    return (
      <SettingsViewWrapper
        sectionId="returns-settings"
        activeNav="resolution-portal/returns/incentives"
      >
        <UnsavedChangesGuard
          hasUnsavedChanges={hasUnsavedChanges || currentExchange.dirty}
          onSave={saveAllSettings}
        />
        <div className="w-full h-full bg-[#f6f6f7]">
          <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
            <ReturnsSettingsTabs activeTab="incentives" />

            {/* Incentive Settings Content */}
            <div className="sticky top-0 z-20 -mx-2 flex flex-col gap-4 border-b border-gray-200 bg-[#f6f6f7]/95 px-2 py-3 backdrop-blur-sm md:flex-row md:items-start md:justify-between">
              <div>
                <SharedResolutionSettingsNotice />
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Incentives
                </h1>
                <p className="text-gray-500 mt-1 max-w-2xl text-sm">
                  {
                    'Offer optional incentives for returns and exchanges.'
                  }
                </p>
              </div>
              <div className="flex items-center gap-2">
                {(hasUnsavedChanges || currentExchange.dirty) && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-amber-700"
                  >
                    Unsaved changes
                  </span>
                )}
                {saveStatus === 'saved' && !currentExchange.dirty && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-green-700"
                  >
                    Saved
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span
                    role="status"
                    className="text-xs font-semibold text-red-700"
                  >
                    Not saved
                  </span>
                )}
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5"></span>
                  Live
                </span>
                <Button
                  size={SizeEnum.sm}
                  onClick={saveAllSettings}
                  isLoading={updateLoading || combinedSaving || currentExchange.saving}
                  disabled={updateLoading || combinedSaving || currentExchange.saving}
                >
                  {updateLoading || combinedSaving || currentExchange.saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Main Column */}
              <div className="lg:col-span-8 flex flex-col gap-6">
                <ExchangeIncentiveSettings key={storeId} disabled={combinedSaving || updateLoading} onSaveStateChange={value => {
                  if (!pageMounted.current || pageOwner.current !== pageScope) return;
                  exchangeApi.current = {...value, scope: pageScope};
                  setExchangeState({scope: pageScope, dirty: value.dirty, saving: value.saving});
                }} />
                <h2 className="text-lg font-semibold text-gray-900">Return incentives</h2>
                <fieldset disabled={combinedSaving || updateLoading} className="min-w-0 space-y-6">
                  <legend className="sr-only">Return incentives</legend>
                {storeCreditNeedsReconnect && (
                  <Alert
                    message="Reconnect Shopify to enable store credit."
                    type="warning"
                    actions={[
                      <Button
                        key="reconnect-shopify"
                        type="button"
                        onClick={handleReconnectShopify}
                        isLoading={reconnectingShopify}
                      >
                        Reconnect
                      </Button>,
                    ]}
                  />
                )}

                <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {orderedOverviewCards.map((card) => (
                    <div
                      key={card.title}
                      className={classNames(
                        'rounded-xl border px-4 py-3 shadow-sm',
                        card.tone === 'blue' && 'border-blue-100 bg-blue-50/80',
                        card.tone === 'green' &&
                          'border-green-100 bg-green-50/80',
                        card.tone === 'gray' && 'border-gray-200 bg-white',
                      )}
                    >
                      <p className="text-xs font-medium text-gray-500">
                        {card.title}
                      </p>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        {card.value}
                      </p>
                    </div>
                  ))}
                </section>

                {/* Keep-item offers */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="max-w-2xl">
                      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <TrashIcon className="w-5 h-5 text-blue-500" />
                        Keep-item offers
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Offer credit or a discount to avoid a return.
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2 self-start">
                      <div className="flex items-center gap-3 rounded-full border border-gray-200 bg-gray-50 px-3 py-2">
                        <span className="hidden sm:inline-flex items-center justify-center rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-xs font-medium text-blue-700 whitespace-nowrap">
                          {deflectionEnabled
                            ? `${activeDeflectionRules.length} active rule${
                                activeDeflectionRules.length === 1 ? '' : 's'
                              }`
                            : 'Off'}
                        </span>
                        <Toggle
                          isEnabled={deflectionEnabled}
                          onChange={setDeflectionEnabled}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      'p-6 space-y-6',
                      !deflectionEnabled && 'opacity-70',
                    )}
                  >
                    <div className="space-y-4">
                      {rules.map((rule, ruleIndex) => (
                        <div
                          key={rule.id}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <span className="bg-white border border-gray-200 text-gray-500 text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full">
                                {ruleIndex + 1}
                              </span>
                              <input
                                type="text"
                                value={rule.name}
                                maxLength={INCENTIVE_NAME_MAX_LENGTH}
                                onChange={(e) => {
                                  const newRules = [...rules];
                                  newRules[ruleIndex].name = e.target.value;
                                  setRules(newRules);
                                }}
                                className="text-sm font-semibold text-gray-900 bg-transparent border-none focus:ring-0 p-0"
                                placeholder="Rule label"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1">
                                <Toggle
                                  isEnabled={rule.isEnabled !== false}
                                  onChange={(nextEnabled) =>
                                    setRules((currentRules) =>
                                      currentRules.map((currentRule) =>
                                        currentRule.id === rule.id
                                          ? {
                                              ...currentRule,
                                              isEnabled: nextEnabled,
                                            }
                                          : currentRule,
                                      ),
                                    )
                                  }
                                  isSmall
                                  ariaLabel={`Enable ${
                                    rule.name || `rule ${ruleIndex + 1}`
                                  }`}
                                />
                                <span className="text-xs font-medium text-gray-600">
                                  {rule.isEnabled !== false
                                    ? 'Enabled'
                                    : 'Paused'}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() =>
                                    setRules(
                                      moveListItem(
                                        rules,
                                        ruleIndex,
                                        ruleIndex - 1,
                                      ),
                                    )
                                  }
                                  disabled={ruleIndex === 0}
                                  className={classNames(
                                    'inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 transition-colors',
                                    ruleIndex === 0
                                      ? 'cursor-not-allowed opacity-40'
                                      : 'hover:border-gray-300 hover:text-gray-700',
                                  )}
                                  title="Move rule up"
                                >
                                  <ArrowUpIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    setRules(
                                      moveListItem(
                                        rules,
                                        ruleIndex,
                                        ruleIndex + 1,
                                      ),
                                    )
                                  }
                                  disabled={ruleIndex === rules.length - 1}
                                  className={classNames(
                                    'inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 transition-colors',
                                    ruleIndex === rules.length - 1
                                      ? 'cursor-not-allowed opacity-40'
                                      : 'hover:border-gray-300 hover:text-gray-700',
                                  )}
                                  title="Move rule down"
                                >
                                  <ArrowDownIcon className="w-4 h-4" />
                                </button>
                              </div>
                              <button
                                onClick={() => {
                                  const newRules = rules.filter(
                                    (r) => r.id !== rule.id,
                                  );
                                  setRules(newRules);
                                }}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          <div
                            className={classNames(
                              'p-4 space-y-4',
                              rule.isEnabled === false && 'opacity-60',
                            )}
                          >
                            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-blue-600">
                                Customer preview
                              </p>
                              <p className="text-sm text-blue-900 leading-relaxed mt-1.5">
                                {summarizeDeflectionRule(currency, rule)}
                              </p>
                            </div>

                            {/* Conditions */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                                  Return value
                                </label>
                                <div className="flex items-center gap-2">
                                  <div className="relative flex-1">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">
                                      $
                                    </span>
                                    <NonNegativeNumberInput
                                      value={rule.minItemValue}
                                      onChange={(e) => {
                                        const newRules = [...rules];
                                        newRules[ruleIndex].minItemValue =
                                          Number(e.target.value);
                                        setRules(newRules);
                                      }}
                                      className="block w-full pl-6 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-black focus:border-black"
                                      placeholder="Minimum return value"
                                    />
                                  </div>
                                  <span className="text-gray-400">-</span>
                                  <div className="relative flex-1">
                                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">
                                      $
                                    </span>
                                    <NonNegativeNumberInput
                                      value={rule.maxItemValue || ''}
                                      onChange={(e) => {
                                        const newRules = [...rules];
                                        newRules[ruleIndex].maxItemValue = e
                                          .target.value
                                          ? Number(e.target.value)
                                          : null;
                                        setRules(newRules);
                                      }}
                                      className="block w-full pl-6 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-black focus:border-black"
                                      placeholder="Maximum return value"
                                    />
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1.5">
                                  Based on the selected items total. Leave the
                                  maximum blank for no limit.
                                </p>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                                  Delivery Guarantee
                                </label>
                                <label className="flex items-center gap-2 p-2 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50">
                                  <input
                                    type="checkbox"
                                    checked={rule.requiresProtection}
                                    onChange={(e) => {
                                      const newRules = [...rules];
                                      newRules[ruleIndex].requiresProtection =
                                        e.target.checked;
                                      setRules(newRules);
                                    }}
                                    className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                                  />
                                  <span className="text-sm text-gray-700">
                                    Require SHIPAID Delivery Guarantee
                                  </span>
                                </label>
                              </div>
                            </div>

                            <IncentiveItemEligibilityEditor
                              value={rule.eligibility}
                              storeId={storeId}
                              ruleId={rule.id}
                              onChange={(eligibility) =>
                                setRules((currentRules) =>
                                  currentRules.map((currentRule) =>
                                    currentRule.id === rule.id
                                      ? { ...currentRule, eligibility }
                                      : currentRule,
                                  ),
                                )
                              }
                            />

                            {/* Incentives Options */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                                Customer offers
                              </label>
                              <div className="space-y-3">
                                {rule.options.map((option, optIndex) => (
                                  <div
                                    key={option.id}
                                    className="flex items-center gap-3 bg-gray-50 p-2 rounded-md border border-gray-200"
                                  >
                                    <select
                                      value={option.offerType}
                                      onChange={(e) => {
                                        const newRules = [...rules];
                                        newRules[ruleIndex].options[
                                          optIndex
                                        ].offerType = e.target
                                          .value as IncentiveOfferType;
                                        setRules(newRules);
                                      }}
                                      className="text-sm border-gray-300 rounded-md focus:ring-black focus:border-black"
                                    >
                                      <option
                                        value={IncentiveOfferType.STORE_CREDIT}
                                      >
                                        Store Credit
                                      </option>
                                      <option
                                        value={IncentiveOfferType.DISCOUNT_CODE}
                                      >
                                        Discount Code
                                      </option>
                                    </select>

                                    <div className="flex-1 relative">
                                      <NonNegativeNumberInput
                                        value={option.value}
                                        onChange={(e) => {
                                          const newRules = [...rules];
                                          newRules[ruleIndex].options[
                                            optIndex
                                          ].value = normalizeIncentiveValue(
                                            Number(e.target.value),
                                            option.valueType,
                                          );
                                          setRules(newRules);
                                        }}
                                        min="0"
                                        max={
                                          option.valueType ===
                                          IncentiveValueType.PERCENTAGE
                                            ? 100
                                            : undefined
                                        }
                                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-black focus:border-black"
                                      />
                                      <div className="absolute inset-y-0 right-0 flex items-center">
                                        <select
                                          value={option.valueType}
                                          onChange={(e) => {
                                            const newRules = [...rules];
                                            const nextValueType = e.target
                                              .value as IncentiveValueType;
                                            const nextOption =
                                              newRules[ruleIndex].options[
                                                optIndex
                                              ];
                                            nextOption.valueType =
                                              nextValueType;
                                            nextOption.value =
                                              normalizeIncentiveValue(
                                                nextOption.value,
                                                nextValueType,
                                              );
                                            setRules(newRules);
                                          }}
                                          className="h-full py-0 pl-2 pr-7 text-xs bg-transparent border-none text-gray-500 focus:ring-0"
                                        >
                                          <option
                                            value={
                                              IncentiveValueType.PERCENTAGE
                                            }
                                          >
                                            %
                                          </option>
                                          <option
                                            value={
                                              IncentiveValueType.FIXED_AMOUNT
                                            }
                                          >
                                            $
                                          </option>
                                        </select>
                                      </div>
                                    </div>

                                    <button
                                      onClick={() => {
                                        const newRules = [...rules];
                                        newRules[ruleIndex].options = newRules[
                                          ruleIndex
                                        ].options.filter(
                                          (o) => o.id !== option.id,
                                        );
                                        setRules(newRules);
                                      }}
                                      className="text-gray-400 hover:text-red-500"
                                      disabled={rule.options.length === 1}
                                    >
                                      <XIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => {
                                    const newRules = [...rules];
                                    newRules[ruleIndex].options.push({
                                      id: generateId(),
                                      offerType:
                                        IncentiveOfferType.STORE_CREDIT,
                                      value: 10,
                                      valueType: IncentiveValueType.PERCENTAGE,
                                    });
                                    setRules(newRules);
                                  }}
                                  className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                  <PlusIcon className="w-3 h-3" /> Add offer
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        onClick={() => {
                          setRules([
                            ...rules,
                            {
                              id: generateId(),
                              name: `Rule #${rules.length + 1}`,
                              minItemValue: 0,
                              requiresProtection: false,
                              isEnabled: true,
                              options: [
                                {
                                  id: generateId(),
                                  offerType: IncentiveOfferType.STORE_CREDIT,
                                  value: 20,
                                  valueType: IncentiveValueType.PERCENTAGE,
                                },
                              ],
                            },
                          ]);
                        }}
                        className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                      >
                        <PlusIcon className="w-4 h-4" /> Add keep-item rule
                      </button>

                      <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-100 bg-amber-50/70 px-4 py-4">
                        <div className="flex items-start gap-3">
                          <ShieldCheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              Block Keep Your Item offers for high-risk orders
                            </p>
                            <p className="mt-0.5 text-xs text-gray-500">
                              Hide Keep Your Item offers when an order is
                              flagged as high risk.
                            </p>
                          </div>
                        </div>
                        <Toggle
                          isEnabled={smartEligibility}
                          onChange={setSmartEligibility}
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Store-credit bonus */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="max-w-2xl">
                      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                        <SaveAsIcon className="w-5 h-5 text-green-500" />
                        Store credit
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {
                          'Store credit is available at the standard return value. Bonus rules are optional.'
                        }
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2 self-start">
                      <div className="flex items-center gap-3 rounded-full border border-gray-200 bg-gray-50 px-3 py-2">
                        <span className="hidden sm:inline-flex items-center justify-center rounded-full bg-green-50 border border-green-100 px-3 py-1 text-xs font-medium text-green-700 whitespace-nowrap">
                          {refundOptimizationEnabled
                            ? activeOptimizationRules.length > 0
                              ? `${activeOptimizationRules.length} bonus rule${
                                  activeOptimizationRules.length === 1
                                    ? ''
                                    : 's'
                                }`
                              : 'On'
                            : 'Off'}
                        </span>
                        <Toggle
                          isEnabled={refundOptimizationEnabled}
                          onChange={setRefundOptimizationEnabled}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      'p-6 space-y-4',
                      !refundOptimizationEnabled && 'opacity-70',
                    )}
                  >
                    {refundOptimizationEnabled &&
                      optimizationRules.length === 0 && (
                        <div className="rounded-lg border border-dashed border-green-200 bg-green-50/60 px-4 py-4">
                          <p className="text-sm font-medium text-green-900">
                            No bonus rules
                          </p>
                          <p className="mt-1 text-xs leading-relaxed text-green-700">
                            Store credit stays available at the standard return
                            value. Add a rule only when you want to offer extra
                            credit for specific returns.
                          </p>
                        </div>
                      )}

                    {optimizationRules.map((rule, ruleIndex) => (
                      <div
                        key={rule.id}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <span className="bg-white border border-gray-200 text-gray-500 text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full">
                              {ruleIndex + 1}
                            </span>
                            <input
                              type="text"
                              value={rule.name}
                              maxLength={INCENTIVE_NAME_MAX_LENGTH}
                              onChange={(e) => {
                                const newRules = [...optimizationRules];
                                newRules[ruleIndex].name = e.target.value;
                                setOptimizationRules(newRules);
                              }}
                              className="text-sm font-semibold text-gray-900 bg-transparent border-none focus:ring-0 p-0"
                              placeholder="Rule label"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1">
                              <Toggle
                                isEnabled={rule.isEnabled !== false}
                                onChange={(nextEnabled) =>
                                  setOptimizationRules((currentRules) =>
                                    currentRules.map((currentRule) =>
                                      currentRule.id === rule.id
                                        ? {
                                            ...currentRule,
                                            isEnabled: nextEnabled,
                                          }
                                        : currentRule,
                                    ),
                                  )
                                }
                                isSmall
                                ariaLabel={`Enable ${
                                  rule.name || `bonus rule ${ruleIndex + 1}`
                                }`}
                              />
                              <span className="text-xs font-medium text-gray-600">
                                {rule.isEnabled !== false
                                  ? 'Enabled'
                                  : 'Paused'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() =>
                                  setOptimizationRules(
                                    moveListItem(
                                      optimizationRules,
                                      ruleIndex,
                                      ruleIndex - 1,
                                    ),
                                  )
                                }
                                disabled={ruleIndex === 0}
                                className={classNames(
                                  'inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 transition-colors',
                                  ruleIndex === 0
                                    ? 'cursor-not-allowed opacity-40'
                                    : 'hover:border-gray-300 hover:text-gray-700',
                                )}
                                title="Move rule up"
                              >
                                <ArrowUpIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() =>
                                  setOptimizationRules(
                                    moveListItem(
                                      optimizationRules,
                                      ruleIndex,
                                      ruleIndex + 1,
                                    ),
                                  )
                                }
                                disabled={
                                  ruleIndex === optimizationRules.length - 1
                                }
                                className={classNames(
                                  'inline-flex items-center justify-center rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 transition-colors',
                                  ruleIndex === optimizationRules.length - 1
                                    ? 'cursor-not-allowed opacity-40'
                                    : 'hover:border-gray-300 hover:text-gray-700',
                                )}
                                title="Move rule down"
                              >
                                <ArrowDownIcon className="w-4 h-4" />
                              </button>
                            </div>
                            <button
                              onClick={() => {
                                const newRules = optimizationRules.filter(
                                  (r) => r.id !== rule.id,
                                );
                                setOptimizationRules(newRules);
                              }}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <div
                          className={classNames(
                            'p-4 space-y-4',
                            rule.isEnabled === false && 'opacity-60',
                          )}
                        >
                          <div className="rounded-lg border border-green-100 bg-green-50 px-4 py-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-green-600">
                              Customer preview
                            </p>
                            <p className="text-sm text-green-900 leading-relaxed mt-1.5">
                              {summarizeOptimizationRule(currency, rule)}
                            </p>
                          </div>

                          {/* Conditions */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                                Return value
                              </label>
                              <div className="flex items-center gap-2">
                                <div className="relative flex-1">
                                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">
                                    $
                                  </span>
                                  <NonNegativeNumberInput
                                    value={rule.minItemValue}
                                    onChange={(e) => {
                                      const newRules = [...optimizationRules];
                                      newRules[ruleIndex].minItemValue = Number(
                                        e.target.value,
                                      );
                                      setOptimizationRules(newRules);
                                    }}
                                    className="block w-full pl-6 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-black focus:border-black"
                                    placeholder="Minimum return value"
                                  />
                                </div>
                                <span className="text-gray-400">-</span>
                                <div className="relative flex-1">
                                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 text-sm">
                                    $
                                  </span>
                                  <NonNegativeNumberInput
                                    value={rule.maxItemValue || ''}
                                    onChange={(e) => {
                                      const newRules = [...optimizationRules];
                                      newRules[ruleIndex].maxItemValue = e
                                        .target.value
                                        ? Number(e.target.value)
                                        : null;
                                      setOptimizationRules(newRules);
                                    }}
                                    className="block w-full pl-6 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-black focus:border-black"
                                    placeholder="Maximum return value"
                                  />
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1.5">
                                Based on the selected items total. Leave the
                                maximum blank for no limit.
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                                Delivery Guarantee
                              </label>
                              <label className="flex items-center gap-2 p-2 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50">
                                <input
                                  type="checkbox"
                                  checked={rule.requiresProtection}
                                  onChange={(e) => {
                                    const newRules = [...optimizationRules];
                                    newRules[ruleIndex].requiresProtection =
                                      e.target.checked;
                                    setOptimizationRules(newRules);
                                  }}
                                  className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                                />
                                <span className="text-sm text-gray-700">
                                  Require SHIPAID Delivery Guarantee
                                </span>
                              </label>
                            </div>
                          </div>

                          {/* Incentive */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                              Bonus
                            </label>
                            <p className="text-xs text-gray-500 mb-3">
                              Added to the standard store-credit amount.
                            </p>
                            <div className="bg-gray-50 p-3 rounded-md border border-gray-200 max-w-xs">
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <span className="text-gray-500 font-bold">
                                    +
                                  </span>
                                </div>
                                <NonNegativeNumberInput
                                  value={rule.bonusValue}
                                  onChange={(e) => {
                                    const newRules = [...optimizationRules];
                                    newRules[ruleIndex].bonusValue =
                                      normalizePercentageValue(
                                        Number(e.target.value),
                                      );
                                    setOptimizationRules(newRules);
                                  }}
                                  min="0"
                                  max="100"
                                  className="block w-full pl-8 pr-12 py-2 text-sm border border-gray-300 rounded-md focus:ring-black focus:border-black"
                                />
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                  <span className="text-gray-500 text-xs font-medium uppercase">
                                    % Added
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => {
                        setOptimizationRules([
                          ...optimizationRules,
                          {
                            id: generateId(),
                            name: `Bonus Rule #${optimizationRules.length + 1}`,
                            minItemValue: 0,
                            requiresProtection: false,
                            isEnabled: true,
                            bonusValue: 10,
                          },
                        ]);
                      }}
                      className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                    >
                      <PlusIcon className="w-4 h-4" /> Add optional bonus rule
                    </button>
                  </div>
                </section>

                {/* Refund */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <RefreshIcon className="w-5 h-5 text-gray-500" />
                      Refund
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {'Set deductions when the customer chooses a refund.'}
                    </p>
                  </div>
                  <div className="p-6">
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50/70">
                      <div className="divide-y divide-gray-200">
                        <div className="bg-white px-4 py-4">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Return label charge
                              </p>
                              <p className="mt-1 text-xs leading-relaxed text-gray-500">
                                {
                                  'Configured in Shipping and deducted only when the customer pays return shipping.'
                                }
                              </p>
                            </div>
                            <Link
                              to={RETURN_LABEL_CHARGE_SHIPPING_PATH}
                              className="inline-flex w-fit items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
                            >
                              Edit shipping
                            </Link>
                          </div>

                          <div className="mt-4 grid gap-3 md:grid-cols-2">
                            {returnLabelChargeSettings.map((setting) => (
                              <div
                                key={setting.title}
                                className={classNames(
                                  'rounded-lg border px-4 py-3',
                                  setting.tone === 'green'
                                    ? 'border-green-100 bg-green-50'
                                    : 'border-gray-200 bg-gray-50',
                                )}
                              >
                                <div className="flex items-start gap-2">
                                  {setting.tone === 'green' && (
                                    <ShieldCheckIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                                  )}
                                  <div>
                                    <p
                                      className={classNames(
                                        'text-sm font-medium',
                                        setting.tone === 'green'
                                          ? 'text-green-950'
                                          : 'text-gray-900',
                                      )}
                                    >
                                      {setting.title}
                                    </p>
                                    <p
                                      className={classNames(
                                        'mt-0.5 text-xs',
                                        setting.tone === 'green'
                                          ? 'text-green-800/80'
                                          : 'text-gray-500',
                                      )}
                                    >
                                      {setting.description}
                                    </p>
                                    <p
                                      className={classNames(
                                        'mt-2 text-sm font-semibold',
                                        setting.tone === 'green'
                                          ? 'text-green-950'
                                          : 'text-gray-900',
                                      )}
                                    >
                                      {setting.value}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Additional fees */}
                <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="flex flex-col gap-4 border-b border-gray-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        Additional fees
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Set fees for refunds, store credit, or both. Keep-item
                        offers are never charged.
                      </p>
                    </div>
                    <Toggle
                      isEnabled={allowAdditionalFees}
                      onChange={setAllowAdditionalFees}
                    />
                  </div>

                  {allowAdditionalFees && (
                    <div className="space-y-5 p-6">
                      <div className="flex items-center justify-between gap-4 rounded-lg border border-green-100 bg-green-50 px-4 py-4">
                        <div className="flex items-start gap-3">
                          <ShieldCheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              Waive fees for guaranteed orders
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-gray-600">
                              Do not deduct configured fees from guaranteed
                              orders.
                            </p>
                          </div>
                        </div>
                        <Toggle
                          isEnabled={waiveFeesForProtectedOrders}
                          onChange={setWaiveFeesForProtectedOrders}
                        />
                      </div>

                      <div>
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-gray-700">
                              Fees
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              Choose which customer outcome each fee applies to.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleAddFee}
                            className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
                          >
                            <PlusIcon className="h-3 w-3" /> Add fee
                          </button>
                        </div>

                        <div className="mt-3 space-y-3">
                          {dynamicFees.map((fee) => (
                            <DynamicFeeCard
                              key={fee.id}
                              fee={fee}
                              currency={currency}
                              nameMaxLength={INCENTIVE_NAME_MAX_LENGTH}
                              isExpanded={expandedFeeIds.includes(fee.id)}
                              sampleSku={feeSampleSkus[fee.id] || ''}
                              onToggleExpanded={() => toggleFeeEditor(fee.id)}
                              onRemove={() => handleRemoveFee(fee.id)}
                              onChange={(field, value) =>
                                handleFeeChange(fee.id, field, value)
                              }
                              onScopeChange={(scope) =>
                                handleFeeScopeChange(fee.id, scope)
                              }
                              onAddCondition={() =>
                                handleAddFeeCondition(fee.id)
                              }
                              onConditionChange={(conditionId, field, value) =>
                                handleFeeConditionChange(
                                  fee.id,
                                  conditionId,
                                  field,
                                  value,
                                )
                              }
                              onRemoveCondition={(conditionId) =>
                                handleRemoveFeeCondition(fee.id, conditionId)
                              }
                              onSampleSkuChange={(value) =>
                                setFeeSampleSkus((samples) => ({
                                  ...samples,
                                  [fee.id]: value,
                                }))
                              }
                            />
                          ))}

                          {dynamicFees.length === 0 && (
                            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-4">
                              <p className="text-xs text-gray-500">
                                No fees added.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </section>
                </fieldset>
              </div>

              {/* Sidebar Column */}
              <div className="lg:col-span-4 space-y-6">
                {/* Summary */}
                <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-bold text-gray-900">Return incentives summary</h3>
                    <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                      Live
                    </span>
                  </div>
                  <div className="space-y-3">
                    {configurationHighlights.map((item) => (
                      <div
                        key={item.label}
                        className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                      >
                        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                          {item.label}
                        </p>
                        <p className="whitespace-pre-line text-sm text-gray-900 font-medium mt-1.5 leading-relaxed">
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </SettingsViewWrapper>
    );
  },
);

export default IncentiveSettings;
